import * as React from "react";
import { useState, useEffect } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

// Edit Franchise Details (slice A5) -- one coherent administrative concern at the FRANCHISE grain:
// the franchise's active flag and notes.
//
// NO EMAIL FIELD, deliberately. There is no writable franchise email:
//   - lksi_account_rep_email is the BDM's address and belongs to Edit Default Ownership;
//   - dim_franchise.email is warehouse-sourced and never written by Season Manager, so it appears
//     here as READ-ONLY context.
// Default BDM is likewise absent -- it stays read-only on the band until the Track B bdm_id
// migration lands, rather than shipping a free-text editor we intend to replace.
//
// PATCH-style (like the ownership dialog, unlike the facility-details one): only changed fields are
// sent, to the new additive franchise-settings-update endpoint. The legacy franchise-update
// endpoint is a full overwrite and is never called from here.
export default function EditFranchiseDetailsDialog(props) {
  const open = props.open;
  const onClose = props.onClose;
  const franchise = props.franchise;
  const onSave = props.onSave;
  const onSaved = props.onSaved;

  const [isActive, setIsActive] = useState(true);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Re-seed from the current values on every open (dialog contract rule 1).
  useEffect(() => {
    if (!open) return;
    setIsActive(franchise.isActive);
    setNotes(franchise.notes ?? "");
    setError(null);
  }, [open, franchise]);

  const activeChanged = isActive !== franchise.isActive;
  const notesChanged = notes !== (franchise.notes ?? "");
  const hasChanges = activeChanged || notesChanged;

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // Only what changed. An omitted key is preserved server-side, so this never disturbs
      // lksi_pacesetter or the account-rep columns that the legacy endpoint would have rewritten.
      const payload = { dim_franchiseID: franchise.dimFranchiseId };
      if (activeChanged) payload.lksi_is_active = isActive ? 1 : 0;
      if (notesChanged) payload.lksi_notes = notes.trim() === "" ? null : notes;

      await onSave(payload);
      if (onSaved) onSaved(`${franchise.name} details updated`);
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{franchise.name} — Franchise Details</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Com code: <strong>{franchise.comcode}</strong>
            {franchise.sourceEmail ? (
              <>
                {" · "}Warehouse email: <strong>{franchise.sourceEmail}</strong>{" "}
                <em>(read-only)</em>
              </>
            ) : null}
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={isActive}
                onChange={(event) => setIsActive(event.target.checked)}
                disabled={saving}
              />
            }
            label={
              <Typography variant="body2">
                Franchise active {isActive ? "" : "— hidden from the default Active-only view"}
              </Typography>
            }
          />

          <TextField
            label="Franchise notes"
            size="small"
            fullWidth
            multiline
            minRows={4}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            disabled={saving}
          />

          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={saving || !hasChanges} variant="contained">
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
